// src/app/core/config/api-endpoints.ts

export const API_ENDPOINTS = {
  // Authentication endpoints
  auth: {
    login: 'api/CAS/login',
    logout: 'api/CAS/logout',
    signout: 'api/CAS/signout',
    refreshToken: 'api/Authorization/RefreshToken',
    changePassword: 'companies/user/change/password',
    validateToken: 'api/CAS/validate-token'
  },

  // User management
  users: {
    profile: 'api/users/profile',
    updateProfile: 'api/users/profile',
    permissions: 'api/users/permissions',
    list: 'api/users',
    create: 'api/users',
    update: (id: number) => `api/users/${id}`,
    delete: (id: number) => `api/users/${id}`
  },

  // Customer management
  customers: {
    list: 'api/customers',
    create: 'api/customers',
    detail: (id: number) => `api/customers/${id}`,
    update: (id: number) => `api/customers/${id}`,
    delete: (id: number) => `api/customers/${id}`,
    enrollment: 'api/customers/enrollment',
    search: 'api/customers/search',
    planHistory: 'api/Customer/GetPlanHistory',
    purchaseHistory: 'api/report/getPosReport'
  },

  // Orders
  orders: {
    list: 'api/orders',
    create: 'api/orders',
    detail: (id: number) => `api/orders/${id}`,
    update: (id: number) => `api/orders/${id}`,
    cancel: (id: number) => `api/orders/${id}/cancel`,
    status: (id: number) => `api/orders/${id}/status`,
    
    // Shipment orders
    shipmentOrders: 'api/orders/shipmentOrders',
    
    // Shopify orders
    shopifyOrders: 'api/orders/getShopifyOrders',
    shopifyOrderRisks: 'api/orders/getShopifyOrderRisks',
    
    // Order management
    tableHeader: 'api/orders/showTableHeader',
    saveTableHeader: 'api/orders/saveTableHeader',
    orderMetrics: 'api/orders/getOrderMetrics',
    addActivity: 'api/orders/addOrderActvity',
    cancelOrder: 'api/orders/cancelOrder',
    convertOrderType: 'api/Customer/ConvertOrderType',
    
    // Status lists
    statusList: 'api/orders/status-list',
    paymentStatusList: 'api/orders/paymentStatusesList',
    shipmentStatusList: 'api/orders/shipmentStatusesList'
  },

  // Tickets
  tickets: {
    list: 'api/tickets',
    create: 'api/tickets',
    detail: (id: number) => `api/tickets/${id}`,
    update: (id: number) => `api/tickets/${id}`,
    close: (id: number) => `api/tickets/${id}/close`,
    comments: (id: number) => `api/tickets/${id}/comments`,
    addComment: (id: number) => `api/tickets/${id}/comments`
  },

  // Porting
  porting: {
    checkPortability: 'api/porting/check',
    submitRequest: 'api/porting/request',
    list: 'api/porting/requests',
    status: (id: number) => `api/porting/requests/${id}/status`
  },

  // Reports
  reports: {
    dashboard: 'api/reports/dashboard',
    analytics: 'api/reports/analytics',
    export: 'api/reports/export',
    schedule: 'api/reports/schedule',
    purchaseHistory: 'api/report/getPosReport',
    categories: 'api/report/categories',
    types: 'api/report/types/activity',
    interactive: 'api/report/interActiveReport',
    filterMapping: 'api/report/filterMapping',
    ranges: 'api/report/ranges'
  },

  // Settings
  settings: {
    general: 'api/settings/general',
    notifications: 'api/settings/notifications',
    security: 'api/settings/security',
    themes: 'api/settings/themes'
  },

  // System
  system: {
    health: 'health',
    version: 'api/system/version',
    status: 'api/system/status',
    resourcePermissions: 'api/system/resource-permission-detail',
    agentsList: 'api/system/agents-list'
  },

  // File operations
  files: {
    upload: 'api/files/upload',
    download: (id: string) => `api/files/${id}/download`,
    delete: (id: string) => `api/files/${id}`
  }
} as const;

// Base URL configurations for different services
export const BASE_URLS = {
  cas: 'cas', // Will resolve to environment.CAS_BASE_URL
  main: 'main', // Will resolve to environment.BASE_API
  aqsatel: 'aqsatel', // Will resolve to environment.AQSATEL_API
  mingle: 'mingle', // Will resolve to environment.Prepaid_Mingle_API
  unity: 'unity', // Will resolve to environment.UNITY_BUYFLOW
  mingleBuyflow: 'mingleBuyflow' // Will resolve to environment.MINGLE_BUYFLOW
} as const;

// Request configurations for different types of operations
export const REQUEST_CONFIGS = {
  // Standard configurations
  default: {
    timeout: 30000,
    retries: 1,
    showLoading: false
  },

  // Login requests
  auth: {
    timeout: 45000,
    retries: 0,
    showLoading: true,
    baseUrl: 'cas'
  },

  // File uploads
  upload: {
    timeout: 120000,
    retries: 0,
    showLoading: true
  },

  // Reports and analytics (can be slow)
  reports: {
    timeout: 90000,
    retries: 1,
    showLoading: true
  },

  // Quick lookup operations
  lookup: {
    timeout: 10000,
    retries: 2,
    showLoading: false,
    cache: { enabled: true, ttl: 60000 } // Cache for 1 minute
  },

  // Real-time operations
  realtime: {
    timeout: 5000,
    retries: 0,
    showLoading: false
  }
} as const;